import sort from 'fast-sort';
import { firebase } from 'src/firebase';
import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import ShopsDB from 'src/firebase/db/Shops';
import UsersDB from 'src/firebase/db/Users';
import { formatDateToDate } from 'src/helpers/UtilityHelper';
import { BakedBreadTagEntity } from 'src/firebase/db/BakedBreadTags';
import { ReferenceError, InternalError } from 'src/firebase/db/ApplicationError';
import ValidationManager, { ValidationResponse } from 'src/firebase/db/ValidationManager';
export const bakedBreadInitialEntity = {
  id: '',
  name: '',
  image: '',
  is_stopped: false,
  price: 0,
  description: '',
  width: '',
  depth: '',
  height: '',
  best_before: '',
  order: 0,
  is_reservation: false,
  last_notification_time: {} as firebase.firestore.Timestamp,
  shop_id: '',
  shop_ref: {} as firebase.firestore.DocumentReference[],
  baked_bread_tags: [] as BakedBreadTagEntity[],
  created_at: {} as firebase.firestore.Timestamp,
  updated_at: {} as firebase.firestore.Timestamp,
  stats: {
    userCount: 0,
  },
};
export type BakedBreadEntity = typeof bakedBreadInitialEntity;
export const bakedBreadEntityKeys = (Object.keys(
  bakedBreadInitialEntity,
) as unknown) as (keyof BakedBreadEntity)[];

class BakedBreadsDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    this.setSchemaKeys(bakedBreadEntityKeys);
  }

  isRequired(key: string) {
    return ['shop_id', 'name', 'image', 'price', 'description'].includes(key);
  }

  getAllBakedBreads() {
    const bakedBreads: BakedBreadEntity[] = [];
    return this.getConnection()
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const stats = await this.formatStats(data);

            let tags = [] as BakedBreadTagEntity[];
            if (data.baked_bread_tags) {
              const bakedBreadTagsRefs = data.baked_bread_tags as firebase.firestore.DocumentReference[];
              tags = await Promise.all(
                bakedBreadTagsRefs.map(async (ref) => {
                  const tag = (await (await ref.get()).data()) as BakedBreadTagEntity;
                  return tag;
                }),
              );
            }

            bakedBreads.push({
              ...data,
              shop_id: data.shop_ref.id,
              stats: stats,
              baked_bread_tags: tags,
            } as BakedBreadEntity);
          }),
        );
        return this.formatSuccess<BakedBreadEntity[]>(bakedBreads);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async getBakedBreads(shopId: string) {
    const shopRef = await this.getConnection('shops').doc(shopId);
    let bakedBreads: BakedBreadEntity[] = [];
    return this.getConnection()
      .where('shop_ref', '==', shopRef)
      .orderBy('created_at', 'asc')
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = await doc.data();
            const stats = await this.formatStats(data);

            let tags = [] as BakedBreadTagEntity[];
            if (data.baked_bread_tags) {
              const bakedBreadTagsRefs = data.baked_bread_tags as firebase.firestore.DocumentReference[];
              tags = await Promise.all(
                bakedBreadTagsRefs.map(async (ref) => {
                  const tag = (await (await ref.get()).data()) as BakedBreadTagEntity;
                  return tag;
                }),
              );
            }

            bakedBreads.push({
              ...data,
              shop_id: data.shop_ref.id,
              stats: stats,
              baked_bread_tags: tags,
            } as BakedBreadEntity);
          }),
        );
        bakedBreads = sort(bakedBreads.filter((v) => !v.is_reservation)).by([
          {
            asc: ({ order }) => order,
          },
          {
            asc: ({ updated_at }) => formatDateToDate(updated_at).getTime(),
          },
        ]);
        return this.formatSuccess<BakedBreadEntity[]>(bakedBreads);
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async getReservationBakedBreads(shopId: string) {
    const shopRef = await this.getConnection('shops').doc(shopId);
    let bakedBreads: BakedBreadEntity[] = [];
    return this.getConnection()
      .where('shop_ref', '==', shopRef)
      .where('is_reservation', '==', true)
      .orderBy('created_at', 'asc')
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('予約商品のパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = await doc.data();
            const stats = await this.formatStats(data);

            bakedBreads.push({
              ...data,
              shop_id: data.shop_ref.id,
              stats: stats,
            } as BakedBreadEntity);
          }),
        );
        bakedBreads = sort(bakedBreads).by([
          {
            asc: ({ updated_at }) => formatDateToDate(updated_at).getTime(),
          },
        ]);
        return this.formatSuccess<BakedBreadEntity[]>(bakedBreads);
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // shops経由でパンの一覧を取得する
  async getShopBakedBreads(shopId: string) {
    return await this.getConnection('shops')
      .doc(shopId)
      .get()
      .then(async (shopDoc) => {
        const shop = await shopDoc.data();
        const bakedBreadRefs = (shop?.baked_breads as firebase.firestore.DocumentReference[]) || [];
        let bakedBreads = await Promise.all(
          bakedBreadRefs.map(async (ref) => {
            const bakedBreadDoc = await ref.get();

            // 何らかの理由でデータが存在しない場合、空データを返す
            if (!bakedBreadDoc.exists) {
              return bakedBreadInitialEntity;
            }
            const data = await bakedBreadDoc.data();
            const bakedBread = {
              ...data,
              id: bakedBreadDoc.id,
              shop_id: shopDoc.id,
            } as BakedBreadEntity;
            const stats = await this.formatStats(bakedBread);
            return {
              ...bakedBread,
              stats,
            };
          }),
        );
        bakedBreads = bakedBreads.filter((v) => v.id && !v.is_reservation);
        return this.formatSuccess<BakedBreadEntity[]>(bakedBreads);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  async update(query: Partial<BakedBreadEntity> & { is_draft?: boolean }) {
    const { id, shop_id: newShopId, image: newImage, is_draft, baked_bread_tags } = query;
    const filteredQuery = this.filterQuery(query);

    if (!is_draft) {
      const validationErrors = this.getValidationErrors(filteredQuery);
      if (validationErrors) return validationErrors;
    }

    // TODO:: shop_idがundefinedの場合のエラー処理
    const shopDocRef = ShopsDB.getDocumentRef(newShopId!);
    const {
      shop_ref: { id: oldShopId },
      image: oldImage,
    } = await this.getDocument(id!);
    delete filteredQuery['shop_id'];

    // 画像の差分を確認し、アップロードも行う
    let image = oldImage;
    if (newImage && oldImage !== newImage) {
      image = await this.uploadImage(newImage);
    }

    // カテゴリ設定
    const bakedBreadTagsRefs = [] as firebase.firestore.DocumentReference[];
    if (baked_bread_tags) {
      await Promise.all(
        baked_bread_tags.map(async (tag) => {
          bakedBreadTagsRefs.push(this.getConnection('baked_bread_tags').doc(tag.id));
        }),
      );
    }

    const requestQuery = {
      ...filteredQuery,
      shop_ref: shopDocRef,
      baked_bread_tags: bakedBreadTagsRefs,
      updated_at: this.getServerTimeStamp(),
      image,
    };

    const ref = this.getDocumentRef(filteredQuery.id!);
    return ref
      .update(requestQuery)
      .then(async () => {
        const d = await this.getDocument(filteredQuery.id!);
        if (newShopId !== oldShopId) {
          await ShopsDB.addBakedBread(newShopId!, ref);
          await ShopsDB.removeBakedBread(oldShopId, ref);
        }
        return this.formatSuccess(d);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  // 表示順を表示
  async updateBakedBreadOrders(query: { id: string; order: number }[]) {
    return await Promise.all(
      query.map(async ({ id, order }) => {
        await this.getDocumentRef(id).update({ order });
      }),
    )
      .then(() => {
        return this.formatSuccess(query);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  getValidationErrors(query: Partial<BakedBreadEntity>): ValidationResponse | null {
    const validator = new BakedBreadsValidationManager();

    bakedBreadEntityKeys.forEach((key) => {
      if (this.isRequired(key)) {
        validator.isRequired(key, query[key]);
      }
      switch (key) {
        case 'price':
          validator.isDigit(key, query[key]);
          break;
        case 'description':
          const value = query[key];
          value && validator.validateMaxLength(key, value, 280);
          break;
        case 'baked_bread_tags':
          // 予約商品の場合はカテゴリ未入力を許可する
          if (!query.is_reservation) {
            const tags = query[key];
            tags && validator.isArrayLengthGtZero(key, tags);
          }
          break;
      }
    });
    return validator.isPass() ? null : validator.throwValidateError(validator.getErrors());
  }

  async create(query: Partial<BakedBreadEntity> & { is_draft?: boolean }) {
    const { shop_id, is_draft, baked_bread_tags } = query;
    const filteredQuery = this.filterQuery(query);

    // 予約商品の下書き保存の場合はバリデーションなし
    if (!is_draft) {
      const validationErrors = this.getValidationErrors(filteredQuery);
      if (validationErrors) return validationErrors;
    }

    // TODO:: shop_idがundefinedの場合のエラー処理
    const shopDocRef = ShopsDB.getDocumentRef(shop_id!);
    delete filteredQuery['shop_id'];

    const image = filteredQuery.image ? await this.uploadImage(filteredQuery.image) : null;

    // カテゴリ設定
    const bakedBreadTagsRefs = [] as firebase.firestore.DocumentReference[];
    if (baked_bread_tags) {
      await Promise.all(
        baked_bread_tags.map(async (tag) => {
          bakedBreadTagsRefs.push(this.getConnection('baked_bread_tags').doc(tag.id));
        }),
      );
    }

    const requestQuery = {
      ...filteredQuery,
      id: '',
      shop_ref: shopDocRef,
      baked_bread_tags: bakedBreadTagsRefs,
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
      image,
    };
    console.log('postQuery:bakedBread create', requestQuery);

    const ref = this.getConnection();
    return ref
      .add(requestQuery)
      .then(async (snapshot) => {
        console.log(snapshot);
        const id = snapshot.id;
        await ref.doc(snapshot.id).update({
          id: id,
        });
        await ShopsDB.addBakedBread(shop_id!, snapshot);
        const d = await this.getDocument(id);
        return this.formatSuccess(d);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  formatStats = async (data: firebase.firestore.DocumentData) => {
    const userCount = await UsersDB.getRegisterdBakedBreadsUserCount(data.id);
    return {
      userCount: userCount,
    };
  };
}

class BakedBreadsValidationManager extends ValidationManager {}

export default new BakedBreadsDB({
  schema: 'baked_breads',
});
